/** SVG icons */

import type { h } from 'preact';
import { useConfiguration } from '@components/router/session-context';
import { useImageUrl } from 'client/utils/cdn';

export type SVGProps = h.JSX.SVGAttributes<SVGSVGElement>;

const defaultClass = 'size-4 opacity-75 shrink-0';

/**
 * Render the undo arrow as a raw HTML string so it can be used by direct
 * DOM manipulation without mounting a Preact instance (e.g. in minidoc).
 */
export const undoArrow = (
  className = '',
) => `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="${className} w-6 h-6">
<path stroke-linecap="round" stroke-linejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
</svg>`;

/**
 * Render the redo arrow as a raw HTML string so it can be used by direct
 * DOM manipulation without mounting a Preact instance (e.g. in minidoc).
 */
export const redoArrow = () => undoArrow('-scale-x-100');

/**
 * HeroIcon: sparkles
 */
export function IcoSparkles(props: SVGProps) {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
      />
    </svg>
  );
}

/**
 * HeroIcon: inbox-arrow-down
 */
export function IcoArchive(props: SVGProps) {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" {...props}>
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859M12 3v8.25m0 0l-3-3m3 3l3-3"
      />
    </svg>
  );
}

/**
 * HeroIcon: arrow-up-tray
 */
export function IcoShare(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="2"
      stroke="currentColor"
      class="size-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

/**
 * HeroIcon: rectangle-group
 */
export function IcoRectangleGroup(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="size-4"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 0 1-1.125-1.125v-3.75ZM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-8.25ZM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-2.25Z"
      />
    </svg>
  );
}

/**
 * HeroIcon: percent-badge
 */
export function IcoPercentBadge(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="size-5"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.99 14.993 6-6m6 3.001c0 1.268-.63 2.39-1.593 3.069a3.746 3.746 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043 3.745 3.745 0 0 1-3.068 1.593c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 0 1-3.296-1.043 3.746 3.746 0 0 1-1.043-3.297 3.746 3.746 0 0 1-1.593-3.068c0-1.268.63-2.39 1.593-3.068a3.746 3.746 0 0 1 1.043-3.297 3.745 3.745 0 0 1 3.296-1.042 3.745 3.745 0 0 1 3.068-1.594c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.297 3.746 3.746 0 0 1 1.593 3.068ZM9.74 9.743h.008v.007H9.74v-.007Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
      />
    </svg>
  );
}

/**
 * HeroIcon: archive-box
 */
export function IcoArchiveBox(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="size-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
      />
    </svg>
  );
}

/**
 * HeroIcon: shopping-cart
 */
export function IcoCart(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="size-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
      />
    </svg>
  );
}

/**
 * https://iconmonstr.com/award-17-svg/
 */
export function IcoBadge(props: SVGProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 opacity-75" {...props}>
      <path d="M18 10c0 3.309-2.691 6-6 6s-6-2.691-6-6 2.691-6 6-6 6 2.691 6 6zm4 1.737l-1.895 1.168.687 2.095-2.187.46-.079 2.2-2.213-.304-.84 2.04-1.977-1.031-1.496 1.635-1.497-1.634-1.977 1.031-.84-2.04-2.213.304-.079-2.2-2.186-.461.687-2.095-1.895-1.168 1.374-1.737-1.374-1.737 1.895-1.168-.687-2.095 2.187-.46.079-2.2 2.213.304.84-2.04 1.977 1.031 1.496-1.635 1.497 1.634 1.977-1.031.84 2.04 2.213-.304.079 2.2 2.186.461-.687 2.095 1.895 1.168-1.374 1.737 1.374 1.737zm-3-1.737c0-3.866-3.134-7-7-7s-7 3.134-7 7 3.134 7 7 7 7-3.134 7-7zm-1.859 10.276l2.401 3.724 1.146-2h2.312l-2.655-4.103c-.917.969-1.999 1.775-3.204 2.379zm-13.486-2.379l-2.655 4.103h2.312l1.146 2 2.401-3.724c-1.205-.604-2.287-1.41-3.204-2.379z" />
    </svg>
  );
}

/**
 * HeroIcon: arrow-left
 */
export function IcoArrowLeft(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="size-4 opacity-75"
      {...props}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
    </svg>
  );
}

export function IcoArrowRight({ class: className = 'size-4 opacity-75', ...props }: SVGProps) {
  return <IcoArrowLeft {...props} class={className + ' rotate-180'} />;
}

/**
 * Used for custom fields.
 */
export function IcoDB(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
      />
    </svg>
  );
}

/**
 * HeroIcon: at-symbol
 */
export function IcoAt(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        d="M16.5 12a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 10-2.636 6.364M16.5 12V8.25"
      />
    </svg>
  );
}

export function IcoHide(props: SVGProps) {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="size-4 opacity-75" {...props}>
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  );
}

/**
 * Heroicon: wifi
 */
export function IcoWifi(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z"
      />
    </svg>
  );
}

export function IcoShow(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

/**
 * Heroicon name: folder
 */
export function IcoFolder(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z"
      />
    </svg>
  );
}

/**
 * Heroicon name: face-smile
 */
export function IcoSmile(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z"
      />
    </svg>
  );
}
/**
 * Heroicon name: cube
 */
export function IcoCube(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
      />
    </svg>
  );
}

/**
 * Heroicon name: cog
 */
export function IcoCog(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="size-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12a7.5 7.5 0 0 0 15 0m-15 0a7.5 7.5 0 1 1 15 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077 1.41-.513m14.095-5.13 1.41-.513M5.106 17.785l1.15-.964m11.49-9.642 1.149-.964M7.501 19.795l.75-1.3m7.5-12.99.75-1.3m-6.063 16.658.26-1.477m2.605-14.772.26-1.477m0 17.726-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205 12 12m6.894 5.785-1.149-.964M6.256 7.178l-1.15-.964m15.352 8.864-1.41-.513M4.954 9.435l-1.41-.514M12.002 12l-3.75 6.495"
      />
    </svg>
  );
}

/**
 * Heroicon name: document-text
 */
export function IcoDocument(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

/**
 * Heroicon name: home
 */
export function IcoHome(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

export function IcoCheck(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M5 13l4 4L19 7"
      ></path>
    </svg>
  );
}

/**
 * Heroicon name: adjustments
 */
export function IcoFilter(props: SVGProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" class="size-4 opacity-75" {...props}>
      <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
    </svg>
  );
}

/**
 * Heroicon name: selector
 */
export function IcoSelector(props: SVGProps) {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 9l4-4 4 4m0 6l-4 4-4-4"
      />
    </svg>
  );
}

/**
 * Heroicon name: download
 */
export function IcoDownload(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="size-4 opacity-75 shrink-0"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );
}

/**
 * Heroicon name: download
 */
export function IcoZoomIn(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
      />
    </svg>
  );
}

/**
 * Heroicon name: academic-cap
 */
export function IcoAcademicCap(props: SVGProps) {
  return (
    <svg
      fill="none"
      class="w-4 h-4 opacity-75"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
      />
    </svg>
  );
}

/**
 * iconoir name: sound-high
 */
export function IcoSoundHigh(props: SVGProps) {
  return (
    <svg width="24px" height="24px" viewBox="0 0 24 24" stroke-width="1.5" fill="none" {...props}>
      <path
        d="M1 13.857v-3.714a2 2 0 012-2h2.9a1 1 0 00.55-.165l6-3.956a1 1 0 011.55.835v14.286a1 1 0 01-1.55.835l-6-3.956a1 1 0 00-.55-.165H3a2 2 0 01-2-2z"
        stroke="#000000"
        stroke-width="1.5"
      ></path>
      <path
        d="M17.5 7.5S19 9 19 11.5s-1.5 4-1.5 4M20.5 4.5S23 7 23 11.5s-2.5 7-2.5 7"
        stroke="#000000"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      ></path>
    </svg>
  );
}

/**
 * iconoir name: sound-off
 */
export function IcoSoundOff(props: SVGProps) {
  return (
    <svg width="24px" height="24px" viewBox="0 0 24 24" stroke-width="1.5" fill="none" {...props}>
      <g clip-path="url(#sound-off_svg__clip0_3173_16686)" stroke="#000000" stroke-width="1.5">
        <path
          d="M18 14l2-2m2-2l-2 2m0 0l-2-2m2 2l2 2"
          stroke-linecap="round"
          stroke-linejoin="round"
        ></path>
        <path d="M2 13.857v-3.714a2 2 0 012-2h2.9a1 1 0 00.55-.165l6-3.956a1 1 0 011.55.835v14.286a1 1 0 01-1.55.835l-6-3.956a1 1 0 00-.55-.165H4a2 2 0 01-2-2z"></path>
      </g>
      <defs>
        <clipPath id="sound-off_svg__clip0_3173_16686">
          <path fill="#fff" d="M0 0h24v24H0z"></path>
        </clipPath>
      </defs>
    </svg>
  );
}

/**
 * Heroicon name: exclamation
 */
export function IcoExclamation(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

/**
 * Heroicon name: check-circle
 */
export function IcoCheckCircle(props: SVGProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 opacity-75" {...props}>
      <path
        fill-rule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

/**
 * Heroicon name: x
 */
export function IcoX(props: SVGProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" class="size-4 opacity-75" {...props}>
      <path
        fill-rule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

/**
 * Heroicon name: printer
 */
export function IcoPrinter(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
      />
    </svg>
  );
}

/**
 * Heroicon name: x-circle
 */
export function IcoXCircle(props: SVGProps) {
  return (
    <svg
      class="w-4 h-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: user
 */
export function IcoUser(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

/**
 * Heroicon name: user-circle
 */
export function IcoUserCircle(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="size-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  );
}

/**
 * Heroicon name: trash
 */
export function IcoTrash(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

/**
 * Heroicon name: refresh
 */
export function IcoRefresh(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

/**
 * Heroicon name: chevron-down
 */
export function IcoChevronDown(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/**
 * Heroicon name: chevron-up
 */
export function IcoChevronUp(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
    </svg>
  );
}

/**
 * Heroicon name: chevron-left
 */
export function IcoChevronLeft(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
  );
}

/**
 * Heroicon name: chevron-right
 */
export function IcoChevronRight(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75 stroke-2"
      {...props}
    >
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

/**
 * Heroicon name: credit-card
 */
export function IcoCreditCard(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}

/**
 * Heroicon name: tag
 */
export function IcoTag(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
      />
    </svg>
  );
}

/**
 * Ruzuku logo.
 */
export function IcoRuzukuLogo(props: SVGProps) {
  return (
    <svg fill="#4338CA" viewBox="0 0 389.67 96.596" {...props}>
      <path d="M37.914 41.04a36.081 36.081 0 0 0-4.15-1.451c-1.529-.44-3.367-.66-5.515-.66-3.717 0-6.566 1.165-8.549 3.497-1.982 2.331-2.973 6.224-2.973 11.678v40.908h-4.585c-3.717 0-6.67-.967-8.859-2.903C1.094 90.174 0 87.36 0 83.664V50.937c0-3.519.454-6.972 1.363-10.359.909-3.387 2.354-6.356 4.336-8.907 1.983-2.551 4.543-4.619 7.682-6.202 3.14-1.584 6.897-2.376 11.275-2.376 2.56 0 4.73.352 6.505 1.056 1.776.704 3.2 1.606 4.275 2.705a10.164 10.164 0 0 1 2.354 3.761c.495 1.408.743 2.771.743 4.091v6.334h-.62zM106.555 63.078c0 22.345-9.747 33.518-29.241 33.518-4.13 0-7.992-.704-11.585-2.111a25.749 25.749 0 0 1-9.354-6.269c-2.644-2.77-4.73-6.158-6.257-10.16-1.529-4.003-2.293-8.6-2.293-13.79V23.752h3.098c4.79 0 8.28 1.056 10.47 3.167 2.188 2.112 3.283 5.279 3.283 9.501V62.55c0 6.51 1.3 11.195 3.903 14.054 2.602 2.859 5.802 4.288 9.602 4.288 3.47 0 6.278-1.341 8.425-4.024 2.148-2.684 3.222-7.06 3.222-13.13V23.752H92.8c4.874 0 8.384 1.056 10.532 3.167 2.148 2.112 3.221 5.279 3.221 9.501v26.657zM172.223 86.7c0 2.198-.888 4.134-2.664 5.805-1.776 1.672-3.738 2.508-5.886 2.508h-32.586c-3.882 0-6.752-.858-8.61-2.574-1.86-1.715-2.789-3.98-2.789-6.796 0-2.727.91-5.74 2.726-9.039 1.817-3.299 4.296-6.796 7.434-10.491l16.85-19.53a227.28 227.28 0 0 0 3.16-4.025c1.116-1.452 1.962-2.793 2.54-4.025h-32.833v-5.41c0-2.64.908-4.861 2.725-6.664 1.818-1.804 4.585-2.706 8.302-2.706h30.356c1.982 0 3.634.286 4.956.858 1.321.572 2.374 1.298 3.16 2.177a8.653 8.653 0 0 1 1.734 2.904 9.483 9.483 0 0 1 .557 3.167c0 2.815-.702 5.828-2.106 9.04-1.404 3.21-3.552 6.487-6.443 9.83L146.08 70.996a188.538 188.538 0 0 0-3.469 4.354c-1.156 1.496-2.106 2.991-2.85 4.487h32.463v6.862zM243.962 63.078c0 22.345-9.747 33.518-29.241 33.518-4.13 0-7.992-.704-11.585-2.111a25.749 25.749 0 0 1-9.354-6.269c-2.644-2.77-4.73-6.158-6.257-10.16-1.529-4.003-2.293-8.6-2.293-13.79V23.752h3.098c4.79 0 8.28 1.056 10.47 3.167 2.188 2.112 3.283 5.279 3.283 9.501V62.55c0 6.51 1.3 11.195 3.903 14.054 2.602 2.859 5.802 4.288 9.602 4.288 3.47 0 6.278-1.341 8.425-4.024 2.148-2.684 3.222-7.06 3.222-13.13V23.752h2.973c4.874 0 8.384 1.056 10.532 3.167 2.148 2.112 3.221 5.279 3.221 9.501v26.657zM266.016 0c4.047 0 6.959 1.1 8.735 3.3 1.775 2.198 2.663 5.014 2.663 8.445v42.491c3.222-4.486 6.464-8.93 9.727-13.328a119.965 119.965 0 0 1 10.717-12.536c1.983-2.024 4.027-3.3 6.133-3.827 2.107-.528 4.481-.792 7.125-.792h9.54v.66c-1.156 1.056-2.912 2.903-5.266 5.542a351.02 351.02 0 0 0-7.496 8.71 588.35 588.35 0 0 0-7.868 9.633c-2.601 3.255-4.77 6.07-6.504 8.446 2.643 3.519 5.307 7.038 7.991 10.557a149.486 149.486 0 0 0 8.488 10.16c1.486 1.584 2.808 2.904 3.964 3.96 1.157 1.055 2.23 1.913 3.222 2.573.991.66 1.94 1.187 2.85 1.583.908.396 1.858.77 2.85 1.122-.91 2.815-2.148 4.97-3.718 6.466-1.57 1.496-4.089 2.243-7.558 2.243-1.9 0-3.84-.44-5.823-1.32-1.983-.879-3.676-2.067-5.08-3.562a190.382 190.382 0 0 1-12.08-14.78 900.373 900.373 0 0 1-11.214-15.571V94.88l-5.327.131c-7.6.088-11.4-4.09-11.4-12.536V0h5.329zM389.67 63.078c0 22.345-9.747 33.518-29.241 33.518-4.13 0-7.992-.704-11.585-2.111a25.749 25.749 0 0 1-9.354-6.269c-2.644-2.77-4.73-6.158-6.258-10.16-1.528-4.003-2.292-8.6-2.292-13.79V23.752h3.098c4.79 0 8.28 1.056 10.47 3.167 2.188 2.112 3.283 5.279 3.283 9.501V62.55c0 6.51 1.3 11.195 3.903 14.054 2.602 2.859 5.802 4.288 9.602 4.288 3.47 0 6.278-1.341 8.425-4.024 2.148-2.684 3.222-7.06 3.222-13.13V23.752h2.973c4.874 0 8.384 1.056 10.532 3.167 2.148 2.112 3.221 5.279 3.221 9.501v26.657z" />
    </svg>
  );
}

/**
 * A tenant's custom logo.
 * If icoURL is not defined, display the default Ruzuku logo
 */
export function IcoLogo({
  class: className,
  url,
  ...props
}: SVGProps & { bg?: string; url?: string }) {
  const configuration = useConfiguration();
  const logoUrl = useImageUrl(url);
  if (logoUrl) {
    return <img class={`${className || ''}`} src={logoUrl} />;
  } else if (configuration?.tenant.isCore) {
    return <IcoRuzukuLogo class={className} {...props} />;
  }
  return null;
}

/**
 * Icomonstr: pin-2-svg
 */
export function IcoPinned(props: SVGProps) {
  return (
    <svg fill="currentColor" viewBox="0 0 24 24" stroke="none" {...props}>
      <path d="M11 17h2v5l-2 2v-7zm7-2h-12c0-3.128.091-4.744 1.874-7.276.551-.783.915-1.3.915-2.373 0-2.372-1.789-1.695-1.789-5.351h10c0 3.616-1.789 3.005-1.789 5.35 0 1.073.364 1.59.915 2.374 1.785 2.535 1.874 4.154 1.874 7.276zm-9.968-2h7.936c-.298-4.376-2.756-4.142-2.756-7.649-.001-1.605.521-2.351 1.271-3.351h-4.966c.75 1 1.272 1.745 1.272 3.35 0 3.487-2.46 3.29-2.757 7.65z" />
    </svg>
  );
}

/**
 * Heroicon: lock-closed
 */
export function IcoLock(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

/**
 * Heroicon: key
 */
export function IcoKey(props: SVGProps) {
  return (
    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
      />
    </svg>
  );
}

/**
 * The course placeholder logo.
 */
export function IcoCoursePlaceholder({ class: className, ...props }: SVGProps) {
  return (
    <svg
      class={`${className || ''} bg-gray-400 rounded-lg`}
      viewBox="0 0 24 24"
      stroke-width="2"
      {...props}
    >
      <path
        stroke="#fff"
        fill="none"
        stroke-linecap="round"
        d="M10,17 L10,11 Q 10,7.25 14.5,8"
      ></path>
    </svg>
  );
}

/**
 * The gripper icon, a grid of 3x2 dots
 */
export function IcoGripper(props: SVGProps) {
  return (
    <svg class="w-4 h-4" viewBox="0 0 4 6" stroke="currentColor" {...props}>
      <path
        fill="currentColor"
        stroke-width="1"
        strokeLinecap="round"
        d="M1,1h0 M3,1h0 M1,3h0 M3,3h0 M1,5h0 M3,5h0"
      ></path>
    </svg>
  );
}

/**
 * The gripper icon, a grid of 2x3 dots
 */
export function IcoGripperHorizontal(props: SVGProps) {
  return (
    <svg class="w-4 h-4" viewBox="0 0 6 4" stroke="currentColor" {...props}>
      <path
        fill="currentColor"
        stroke-width="1"
        strokeLinecap="round"
        d="M1,1h0 M3,1h0 M5,1h0 M1,3h0 M3,3h0 M5,3h0"
      ></path>
    </svg>
  );
}

/**
 * The grid icon.
 */
export function IcoGrid(props: SVGProps) {
  return (
    <svg class="w-4 h-4" viewBox="0 0 6 5" stroke="currentColor" {...props}>
      <path
        fill="currentColor"
        stroke-width="1"
        strokeLinecap="round"
        d="M1,1h0 M3,1h0 M5,1h0 M1,3h0 M3,3h0 M5,3h0 M1,5h0 M3,5h0 M5,5h0"
      ></path>
    </svg>
  );
}

/**
 * Heroicon name: users
 */
export function IcoUsers(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: photograph
 */
export function IcoImage(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Heroicon name: search
 */
export function IcoSearch(props: SVGProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      width={12}
      height={12}
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: paperclip
 */
export function IcoPaperclip(props: SVGProps) {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
      />
    </svg>
  );
}

/**
 * Heroicon name: clipboard
 */
export function IcoClipboard(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="size-4 opacity-75 shrink-0"
      strokeWidth={2}
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
      />
    </svg>
  );
}

/**
 * Heroicon name: document-duplicate
 */
export function IcoDuplicate(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      strokeWidth={2}
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
      />
    </svg>
  );
}

/**
 * Heroicon name: mail
 */
export function IcoMail(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Heroicon name: check-badge
 */
export function IcoCheckBadge(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z"
      />
    </svg>
  );
}

/**
 * Heroicon name: pencil
 */
export function IcoPencil(props: SVGProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 p-px opacity-75" {...props}>
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
  );
}

/**
 * Heroicon name: pencil-square
 */
export function IcoPencilSquare(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

/**
 * Heroicon name: library
 */
export function IcoLibrary(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
      />
    </svg>
  );
}

/**
 * Heroicon name: information-circle
 */
export function IcoInfo(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-4"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
      />
    </svg>
  );
}

/**
 * Heroicon name: book-open
 */
export function IcoBook(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

/**
 * Heroicon name: book-open-solid
 */
export function IcoBookSolid(props: SVGProps) {
  return (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
    </svg>
  );
}

/**
 * Heroicon name: chat-alt-2
 */
export function IcoChat(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
      />
    </svg>
  );
}

/**
 * Heroicon name: chat-alt-2-solid
 */
export function IcoChatSolid(props: SVGProps) {
  return (
    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
      <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
    </svg>
  );
}

/**
 * Heroicon name: chat
 */
export function IcoReply(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
      />
    </svg>
  );
}

/**
 * Heroicon name: cog
 */
export function IcoSettings(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: eye
 */
export function IcoEye(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

/**
 * Heroicon name: bell
 */
export function IcoBell(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

/**
 * Heroicon name: menu
 */
export function IcoList(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

/**
 * Heroicon name: dots-horizontal
 */
export function IcoDotsHorizontal(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
      />
    </svg>
  );
}

export function IcoDotsVertical({ class: className = 'h-4 w-4 opacity-75', ...props }: SVGProps) {
  return <IcoDotsHorizontal {...props} class={className + ' rotate-90'} />;
}

/**
 * Heroicon name: heart-solid
 */
export function IcoHeartSolid(props: SVGProps) {
  return (
    <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path
        fillRule="evenodd"
        d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Heroicon name: heart
 */
export function IcoHeart(props: SVGProps) {
  return (
    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: speakerphone-solid
 */
export function IcoAnnouncement(props: SVGProps) {
  return (
    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path
        fillRule="evenodd"
        d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Heroicon name: play-solid
 */
export function IcoPlay(props: SVGProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" class="size-4 opacity-75" {...props}>
      <path
        fillRule="evenodd"
        d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Heroicon name: play-circle
 */
export function IcoPlayCircle(props: SVGProps) {
  return (
    <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path
        fillRule="evenodd"
        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm14.024-.983a1.125 1.125 0 0 1 0 1.966l-5.603 3.113A1.125 1.125 0 0 1 9 15.113V8.887c0-.857.921-1.4 1.671-.983l5.603 3.113Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Heroicon name: play-solid
 */
export function IcoMusic(props: SVGProps) {
  return (
    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
    </svg>
  );
}

/**
 * Icomonstr send
 */
export function IcoSend(props: SVGProps) {
  return (
    <svg class="w-4 h-4 opacity-75" viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M0 12l11 3.1 7-8.1-8.156 5.672-4.312-1.202 15.362-7.68-3.974 14.57-3.75-3.339-2.17 2.925v-.769l-2-.56v7.383l4.473-6.031 4.527 4.031 6-22z" />
    </svg>
  );
}

/**
 * Heroicon name: arrow-circle-left
 */
export function IcoArrowCircleLeft(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      class="h-4 w-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: arrows-expand
 */
export function IcoFullSize(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
      />
    </svg>
  );
}

/**
 * Fontawesome name: up-right-and-down-left-from-center
 */
export function IcoExpand(props: SVGProps) {
  return (
    <svg class="h-4 w-4 opacity-75" viewBox="0 0 512 512" stroke="currentColor" {...props}>
      <path d="M344 0H488c13.3 0 24 10.7 24 24V168c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39-87 87c-9.4 9.4-24.6 9.4-33.9 0l-32-32c-9.4-9.4-9.4-24.6 0-33.9l87-87L327 41c-6.9-6.9-8.9-17.2-5.2-26.2S334.3 0 344 0zM168 512H24c-13.3 0-24-10.7-24-24V344c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39 87-87c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8z" />
    </svg>
  );
}

/**
 * Fontawesome name: down-left-and-up-right-to-center
 */
export function IcoShrink(props: SVGProps) {
  return (
    <svg class="h-4 w-4 opacity-75" viewBox="0 0 512 512" stroke="currentColor" {...props}>
      <path d="M439 7c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H296c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39L439 7zM72 272H216c13.3 0 24 10.7 24 24V440c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39L73 505c-9.4 9.4-24.6 9.4-33.9 0L7 473c-9.4-9.4-9.4-24.6 0-33.9l87-87L55 313c-6.9-6.9-8.9-17.2-5.2-26.2s12.5-14.8 22.2-14.8z" />
    </svg>
  );
}

/**
 * Heroicon name: calendar
 */
export function IcoCalendar(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Heroicon name: user-group
 */
export function IcoUserGroup(props: SVGProps) {
  return (
    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: presentation-chart-bar
 */
export function IcoPresentation(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
      />
    </svg>
  );
}

/**
 * Heroicon name: video-camera
 */
export function IcoVideoCamera(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Heroicon name: presentation-chart-line
 */
export function IcoSlides(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
      />
    </svg>
  );
}

/**
 * Heroicon name: plus
 */
export function IcoPlus(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
    </svg>
  );
}

/**
 * Heroicon name: plus-circle
 */
export function IcoPlusCircle(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}

/**
 * Heroicon name: minus-circle
 */
export function IcoMinusCircle(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}

/**
 * Heroicon name: external-link
 */
export function IcoExternalLink(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

/**
 * Heroicon name: link-slash
 */
export function IcoLinkSlash(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.181 8.68a4.503 4.503 0 0 1 1.903 6.405m-9.768-2.782L3.56 14.06a4.5 4.5 0 0 0 6.364 6.365l3.129-3.129m5.614-5.615 1.757-1.757a4.5 4.5 0 0 0-6.364-6.365l-4.5 4.5c-.258.26-.479.541-.661.84m1.903 6.405a4.495 4.495 0 0 1-1.242-.88 4.483 4.483 0 0 1-1.062-1.683m6.587 2.345 5.907 5.907m-5.907-5.907L8.898 8.898M2.991 2.99 8.898 8.9"
      />
    </svg>
  );
}

/**
 * Heroicon name: phone-outgoing
 */
export function IcoPhoneOutgoing(props: SVGProps) {
  return (
    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 3h5m0 0v5m0-5l-6 6M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z"
      />
    </svg>
  );
}

/**
 * Heroicon name: phone-clock
 */
export function IcoClock(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: globe-alt
 */
export function IcoGlobe(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class={defaultClass}
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
      />
    </svg>
  );
}

/**
 * Heroicon name: map-pin
 */
export function IcoMapPin(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class={defaultClass}
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
      />
    </svg>
  );
}

/**
 * Fontawesome name: google
 */
export function IcoGoogle(props: SVGProps) {
  return (
    <svg class="h-4 w-4 opacity-75" viewBox="0 0 488 512" {...props}>
      <path
        fill="currentColor"
        d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
      ></path>
    </svg>
  );
}

/**
 * Heroicon name: document-search
 */
export function IcoDocumentSearch(props: SVGProps) {
  return (
    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z"
      />
    </svg>
  );
}

/*
 * Downloaded from https://explore.zoom.us/en/media-kit/
 */
export function IcoZoom(props: SVGProps) {
  return (
    <svg viewBox="0 0 356 80" class="h-6" fill="#2d8cff" {...props}>
      <path d="M 144.3,11.7 C 136.5,3.9 126.3,0 116,0 v 0 c -10.2,0 -20.5,3.9 -28.3,11.7 -15.6,15.6 -15.6,40.9 0,56.6 15.6,15.6 40.9,15.6 56.6,0 15.6,-15.6 15.6,-41 0,-56.6 z M 133,57 c -9.4,9.4 -24.6,9.4 -33.9,0 -9.4,-9.4 -9.4,-24.6 0,-33.9 9.4,-9.4 24.6,-9.4 33.9,0 9.3,9.3 9.3,24.5 0,33.9 z" />
      <path d="M 228.3,11.7 C 220.5,3.9 210.3,0 200,0 c -10.2,0 -20.5,3.9 -28.3,11.7 -15.6,15.6 -15.6,40.9 0,56.6 15.6,15.6 40.9,15.6 56.6,0 15.6,-15.6 15.6,-41 0,-56.6 z M 217,57 c -9.4,9.4 -24.6,9.4 -33.9,0 -9.4,-9.4 -9.4,-24.6 0,-33.9 9.4,-9.4 24.6,-9.4 33.9,0 9.3,9.3 9.3,24.5 0,33.9 z" />
      <path d="M 16,79.8 20,80 H 80 L 79.8,76 C 79.3,69.4 74.6,64.6 68,64.2 L 64,64 H 28 L 76,16 75.8,12 C 75.5,5.4 70.6,0.5 64,0.2 L 60,0 H 0 L 0.2,4 C 0.7,10.5 5.5,15.4 12,15.8 L 16,16 H 52 L 4,64 4.2,68 C 4.6,74.6 9.4,79.4 16,79.8 Z" />
      <path d="m 289.8,24 c 1.4,2.4 1.8,5 2,8 l 0.2,4 v 28 l 0.2,4 c 0.4,6.5 5.2,11.4 11.8,11.8 l 4,0.2 V 36 l 0.2,-4 c 0.2,-3 0.6,-5.7 2,-8.1 2.8,-4.8 7.9,-7.9 13.8,-7.9 5.9,0 11.1,3.2 13.9,8 1.4,2.4 1.8,5.1 1.9,8 l 0.2,4 v 28 l 0.2,4 c 0.4,6.6 5.2,11.4 11.8,11.8 l 4,0.2 V 36 32 C 356,14.3 341.7,0 324,0 314.4,0 305.9,4.2 300,10.8 294.1,4.2 285.6,0 276,0 269.4,0 263.2,2 258.1,5.5 255,2 248,0 244,0 v 80 l 4,-0.2 c 6.7,-0.4 11.5,-5.2 11.8,-11.8 L 260,64 V 36 l 0.2,-4 c 0.2,-3 0.6,-5.7 2,-8 2.8,-4.8 7.9,-8 13.8,-8 5.9,0 11.1,3.2 13.8,8 z" />
    </svg>
  );
}

/*
 * Downloaded from https://stripe.com/newsroom/brand-assets
 */
export function IcoStripe(props: SVGProps) {
  return (
    <svg class="h-10 w-auto" viewBox="0 0 360 149.8" fill="#635bff" {...props}>
      <path
        class="st0"
        d="m 360,77.4 c 0,-25.6 -12.4,-45.8 -36.1,-45.8 -23.8,0 -38.2,20.2 -38.2,45.6 0,30.1 17,45.3 41.4,45.3 11.9,0 20.9,-2.7 27.7,-6.5 V 96 c -6.8,3.4 -14.6,5.5 -24.5,5.5 -9.7,0 -18.3,-3.4 -19.4,-15.2 h 48.9 c 0,-1.3 0.2,-6.5 0.2,-8.9 z m -49.4,-9.5 c 0,-11.3 6.9,-16 13.2,-16 6.1,0 12.6,4.7 12.6,16 z"
        id="path54"
      />
      <path
        class="st0"
        d="m 247.1,31.6 c -9.8,0 -16.1,4.6 -19.6,7.8 l -1.3,-6.2 h -22 v 116.6 l 25,-5.3 0.1,-28.3 c 3.6,2.6 8.9,6.3 17.7,6.3 17.9,0 34.2,-14.4 34.2,-46.1 -0.1,-29 -16.6,-44.8 -34.1,-44.8 z m -6,68.9 c -5.9,0 -9.4,-2.1 -11.8,-4.7 l -0.1,-37.1 c 2.6,-2.9 6.2,-4.9 11.9,-4.9 9.1,0 15.4,10.2 15.4,23.3 0,13.4 -6.2,23.4 -15.4,23.4 z"
        id="path56"
      />
      <polygon
        class="st0"
        points="248.9,36 223.8,41.3 223.8,61.7 248.9,56.3 "
        id="polygon58"
        transform="translate(-54,-36)"
      />
      <rect x="169.8" y="33.300003" class="st0" width="25.1" height="87.5" id="rect60" />
      <path
        class="st0"
        d="m 142.9,40.7 -1.6,-7.4 h -21.6 v 87.5 h 25 V 61.5 c 5.9,-7.7 15.9,-6.3 19,-5.2 v -23 c -3.2,-1.2 -14.9,-3.4 -20.8,7.4 z"
        id="path62"
      />
      <path
        class="st0"
        d="m 92.9,11.6 -24.4,5.2 -0.1,80.1 c 0,14.8 11.1,25.7 25.9,25.7 8.2,0 14.2,-1.5 17.5,-3.3 V 99 c -3.2,1.3 -19,5.9 -19,-8.9 V 54.6 h 19 V 33.3 h -19 z"
        id="path64"
      />
      <path
        class="st0"
        d="m 25.3,58.7 c 0,-3.9 3.2,-5.4 8.5,-5.4 7.6,0 17.2,2.3 24.8,6.4 V 36.2 C 50.3,32.9 42.1,31.6 33.8,31.6 13.5,31.6 0,42.2 0,59.9 0,87.5 38,83.1 38,95 c 0,4.6 -4,6.1 -9.6,6.1 -8.3,0 -18.9,-3.4 -27.3,-8 v 23.8 c 9.3,4 18.7,5.7 27.3,5.7 20.8,0 35.1,-10.3 35.1,-28.2 C 63.4,64.6 25.3,69.9 25.3,58.7 Z"
        id="path66"
      />
    </svg>
  );
}

/**
 * Fontawesome name: desktop
 */
export function IcoDesktop(props: SVGProps) {
  return (
    <svg class="w-4 h-4 opacity-75" viewBox="0 0 576 512" {...props}>
      <path
        fill="currentColor"
        d="M528 0H48C21.5 0 0 21.5 0 48v320c0 26.5 21.5 48 48 48h192l-16 48h-72c-13.3 0-24 10.7-24 24s10.7 24 24 24h272c13.3 0 24-10.7 24-24s-10.7-24-24-24h-72l-16-48h192c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zm-16 352H64V64h448v288z"
      />
    </svg>
  );
}

/**
 * Fontawesome name: tablet-alt
 */
export function IcoTablet(props: SVGProps) {
  return (
    <svg class="w-4 h-4" viewBox="0 0 448 512" {...props}>
      <path
        fill="currentColor"
        d="M400 0H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM224 480c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm176-108c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V60c0-6.6 5.4-12 12-12h328c6.6 0 12 5.4 12 12v312z"
      ></path>
    </svg>
  );
}

/**
 * Fontawesome name: mobile-alt
 */
export function IcoMobile(props: SVGProps) {
  return (
    <svg class="w-3 h-4" viewBox="0 0 320 512" {...props}>
      <path
        fill="currentColor"
        d="M272 0H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM160 480c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm112-108c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V60c0-6.6 5.4-12 12-12h200c6.6 0 12 5.4 12 12v312z"
      ></path>
    </svg>
  );
}

/**
 * Fontawesome name: chart-bar-solid
 */
export function IcoChartBar(props: SVGProps) {
  return (
    <svg class="h-4 w-4 opacity-75" viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
    </svg>
  );
}

/**
 * Heroicon name: question-mark-circle
 */
export function IcoQuestion(props: SVGProps) {
  return (
    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Fontawesome name: file-alt
 */
export function IcoFile(props: SVGProps) {
  return (
    <svg class="w-4 h-4 opacity-75" viewBox="0 0 384 512" {...props}>
      <path
        fill="currentColor"
        d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm64 236c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-64c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-72v8c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12zm96-114.1v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"
      ></path>
    </svg>
  );
}

/**
 * Heroicon name: gift
 */
export function IcoGift(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
      />
    </svg>
  );
}

/**
 * Heroicon name: ban
 */
export function IcoBan(props: SVGProps) {
  return (
    <svg
      class="w-4 h-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
      />
    </svg>
  );
}

/**
 * Heroicon name: moon
 */
export function IcoMoon(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
      />
    </svg>
  );
}

/**
 * Heroicon name: sun
 */
export function IcoSun(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: chart-pie
 */
export function IcoChartPie(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      class="w-4 h-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
      />
    </svg>
  );
}

/**
 * Heroicon name: currency-dollar
 */
export function IcoDollar(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      class="h-4 w-4 opacity-75"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Heroicon name: collection
 */
export function IcoCollection(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  );
}

/**
 * Heroicon name: switch-horizontal solid
 */
export function IcoSwitch(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
      />
    </svg>
  );
}

/**
 * Heroicon name: document-magnifying-glass outline
 */
export function IcoMagnifyingGlass(props: SVGProps) {
  return (
    <svg
      class="h-4 w-4 opacity-75"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
      />
    </svg>
  );
}

/**
 * Fontawesome name: caret-leftj
 */
export function IcoCaretLeft(props: SVGProps) {
  return (
    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 256 512" {...props}>
      <path d="M137.4 406.6l-128-127.1C3.125 272.4 0 264.2 0 255.1s3.125-16.38 9.375-22.63l128-127.1c9.156-9.156 22.91-11.9 34.88-6.943S192 115.1 192 128v255.1c0 12.94-7.781 24.62-19.75 29.58S146.5 415.8 137.4 406.6z" />
    </svg>
  );
}

/**
 * Fontawesome name: caret-right
 */
export function IcoCaretRight(props: SVGProps) {
  return (
    <svg class="w-5 h-5" height="1em" viewBox="0 0 256 512" {...props}>
      <path d="M246.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-9.2-9.2-22.9-11.9-34.9-6.9s-19.8 16.6-19.8 29.6l0 256c0 12.9 7.8 24.6 19.8 29.6s25.7 2.2 34.9-6.9l128-128z" />
    </svg>
  );
}

/**
 * Heroicon name: star-solid
 */
export function IcoStar(props: SVGProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="size-5"
      {...props}
    >
      <path
        fillRule="evenodd"
        d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Fontawesome name: banknotes
 */
export function IcoBanknotes(props: SVGProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      class="w-6 h-6"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
      />
    </svg>
  );
}

/**
 * Paypal logo, from here: https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-mark-color.svg
 */
export function IcoPaypal(props: SVGProps) {
  return (
    <svg class="h-10 w-auto" viewBox="0 0 124 33" {...props}>
      <path
        fill="#253B80"
        d="M46.211,6.749h-6.839c-0.468,0-0.866,0.34-0.939,0.802l-2.766,17.537c-0.055,0.346,0.213,0.658,0.564,0.658  h3.265c0.468,0,0.866-0.34,0.939-0.803l0.746-4.73c0.072-0.463,0.471-0.803,0.938-0.803h2.165c4.505,0,7.105-2.18,7.784-6.5  c0.306-1.89,0.013-3.375-0.872-4.415C50.224,7.353,48.5,6.749,46.211,6.749z M47,13.154c-0.374,2.454-2.249,2.454-4.062,2.454  h-1.032l0.724-4.583c0.043-0.277,0.283-0.481,0.563-0.481h0.473c1.235,0,2.4,0,3.002,0.704C47.027,11.668,47.137,12.292,47,13.154z"
      />
      <path
        fill="#253B80"
        d="M66.654,13.075h-3.275c-0.279,0-0.52,0.204-0.563,0.481l-0.145,0.916l-0.229-0.332  c-0.709-1.029-2.29-1.373-3.868-1.373c-3.619,0-6.71,2.741-7.312,6.586c-0.313,1.918,0.132,3.752,1.22,5.031  c0.998,1.176,2.426,1.666,4.125,1.666c2.916,0,4.533-1.875,4.533-1.875l-0.146,0.91c-0.055,0.348,0.213,0.66,0.562,0.66h2.95  c0.469,0,0.865-0.34,0.939-0.803l1.77-11.209C67.271,13.388,67.004,13.075,66.654,13.075z M62.089,19.449  c-0.316,1.871-1.801,3.127-3.695,3.127c-0.951,0-1.711-0.305-2.199-0.883c-0.484-0.574-0.668-1.391-0.514-2.301  c0.295-1.855,1.805-3.152,3.67-3.152c0.93,0,1.686,0.309,2.184,0.892C62.034,17.721,62.232,18.543,62.089,19.449z"
      />
      <path
        fill="#253B80"
        d="M84.096,13.075h-3.291c-0.314,0-0.609,0.156-0.787,0.417l-4.539,6.686l-1.924-6.425  c-0.121-0.402-0.492-0.678-0.912-0.678h-3.234c-0.393,0-0.666,0.384-0.541,0.754l3.625,10.638l-3.408,4.811  c-0.268,0.379,0.002,0.9,0.465,0.9h3.287c0.312,0,0.604-0.152,0.781-0.408L84.564,13.97C84.826,13.592,84.557,13.075,84.096,13.075z  "
      />
      <path
        fill="#179BD7"
        d="M94.992,6.749h-6.84c-0.467,0-0.865,0.34-0.938,0.802l-2.766,17.537c-0.055,0.346,0.213,0.658,0.562,0.658  h3.51c0.326,0,0.605-0.238,0.656-0.562l0.785-4.971c0.072-0.463,0.471-0.803,0.938-0.803h2.164c4.506,0,7.105-2.18,7.785-6.5  c0.307-1.89,0.012-3.375-0.873-4.415C99.004,7.353,97.281,6.749,94.992,6.749z M95.781,13.154c-0.373,2.454-2.248,2.454-4.062,2.454  h-1.031l0.725-4.583c0.043-0.277,0.281-0.481,0.562-0.481h0.473c1.234,0,2.4,0,3.002,0.704  C95.809,11.668,95.918,12.292,95.781,13.154z"
      />
      <path
        fill="#179BD7"
        d="M115.434,13.075h-3.273c-0.281,0-0.52,0.204-0.562,0.481l-0.145,0.916l-0.23-0.332  c-0.709-1.029-2.289-1.373-3.867-1.373c-3.619,0-6.709,2.741-7.311,6.586c-0.312,1.918,0.131,3.752,1.219,5.031  c1,1.176,2.426,1.666,4.125,1.666c2.916,0,4.533-1.875,4.533-1.875l-0.146,0.91c-0.055,0.348,0.213,0.66,0.564,0.66h2.949  c0.467,0,0.865-0.34,0.938-0.803l1.771-11.209C116.053,13.388,115.785,13.075,115.434,13.075z M110.869,19.449  c-0.314,1.871-1.801,3.127-3.695,3.127c-0.949,0-1.711-0.305-2.199-0.883c-0.484-0.574-0.666-1.391-0.514-2.301  c0.297-1.855,1.805-3.152,3.67-3.152c0.93,0,1.686,0.309,2.184,0.892C110.816,17.721,111.014,18.543,110.869,19.449z"
      />
      <path
        fill="#179BD7"
        d="M119.295,7.23l-2.807,17.858c-0.055,0.346,0.213,0.658,0.562,0.658h2.822c0.469,0,0.867-0.34,0.939-0.803  l2.768-17.536c0.055-0.346-0.213-0.659-0.562-0.659h-3.16C119.578,6.749,119.338,6.953,119.295,7.23z"
      />
      <path
        fill="#253B80"
        d="M7.266,29.154l0.523-3.322l-1.165-0.027H1.061L4.927,1.292C4.939,1.218,4.978,1.149,5.035,1.1  c0.057-0.049,0.13-0.076,0.206-0.076h9.38c3.114,0,5.263,0.648,6.385,1.927c0.526,0.6,0.861,1.227,1.023,1.917  c0.17,0.724,0.173,1.589,0.007,2.644l-0.012,0.077v0.676l0.526,0.298c0.443,0.235,0.795,0.504,1.065,0.812  c0.45,0.513,0.741,1.165,0.864,1.938c0.127,0.795,0.085,1.741-0.123,2.812c-0.24,1.232-0.628,2.305-1.152,3.183  c-0.482,0.809-1.096,1.48-1.825,2c-0.696,0.494-1.523,0.869-2.458,1.109c-0.906,0.236-1.939,0.355-3.072,0.355h-0.73  c-0.522,0-1.029,0.188-1.427,0.525c-0.399,0.344-0.663,0.814-0.744,1.328l-0.055,0.299l-0.924,5.855l-0.042,0.215  c-0.011,0.068-0.03,0.102-0.058,0.125c-0.025,0.021-0.061,0.035-0.096,0.035H7.266z"
      />
      <path
        fill="#179BD7"
        d="M23.048,7.667L23.048,7.667L23.048,7.667c-0.028,0.179-0.06,0.362-0.096,0.55  c-1.237,6.351-5.469,8.545-10.874,8.545H9.326c-0.661,0-1.218,0.48-1.321,1.132l0,0l0,0L6.596,26.83l-0.399,2.533  c-0.067,0.428,0.263,0.814,0.695,0.814h4.881c0.578,0,1.069-0.42,1.16-0.99l0.048-0.248l0.919-5.832l0.059-0.32  c0.09-0.572,0.582-0.992,1.16-0.992h0.73c4.729,0,8.431-1.92,9.513-7.476c0.452-2.321,0.218-4.259-0.978-5.622  C24.022,8.286,23.573,7.945,23.048,7.667z"
      />
      <path
        fill="#222D65"
        d="M21.754,7.151c-0.189-0.055-0.384-0.105-0.584-0.15c-0.201-0.044-0.407-0.083-0.619-0.117  c-0.742-0.12-1.555-0.177-2.426-0.177h-7.352c-0.181,0-0.353,0.041-0.507,0.115C9.927,6.985,9.675,7.306,9.614,7.699L8.05,17.605  l-0.045,0.289c0.103-0.652,0.66-1.132,1.321-1.132h2.752c5.405,0,9.637-2.195,10.874-8.545c0.037-0.188,0.068-0.371,0.096-0.55  c-0.313-0.166-0.652-0.308-1.017-0.429C21.941,7.208,21.848,7.179,21.754,7.151z"
      />
      <path
        fill="#253B80"
        d="M9.614,7.699c0.061-0.393,0.313-0.714,0.652-0.876c0.155-0.074,0.326-0.115,0.507-0.115h7.352  c0.871,0,1.684,0.057,2.426,0.177c0.212,0.034,0.418,0.073,0.619,0.117c0.2,0.045,0.395,0.095,0.584,0.15  c0.094,0.028,0.187,0.057,0.278,0.086c0.365,0.121,0.704,0.264,1.017,0.429c0.368-2.347-0.003-3.945-1.272-5.392  C20.378,0.682,17.853,0,14.622,0h-9.38c-0.66,0-1.223,0.48-1.325,1.133L0.01,25.898c-0.077,0.49,0.301,0.932,0.795,0.932h5.791  l1.454-9.225L9.614,7.699z"
      />
    </svg>
  );
}

/**
 * Zapier logo from https://drive.google.com/drive/folders/1RDgyyVDRfPNoKkxfR_J_oTunlD968fDI?hl=en
 */
export function IcoZapier(props: SVGProps) {
  return (
    <svg class="h-10 w-auto" viewBox="0 0 500 229" {...props}>
      <title>zapier-logo</title>
      <path
        d="M300.033 99.59H287.57c-.256-1.02-.447-2.203-.574-3.546a40.666 40.666 0 0 1 0-7.86c.127-1.34.318-2.522.574-3.548h31.06v98.353a46.42 46.42 0 0 1-4.697.572 65.11 65.11 0 0 1-4.7.19 62.93 62.93 0 0 1-4.502-.19 46.28 46.28 0 0 1-4.695-.575v-83.4.002zm108.127 24.734c0-3.58-.48-6.998-1.436-10.26-.96-3.257-2.37-6.1-4.218-8.53-1.857-2.426-4.22-4.377-7.096-5.846-2.875-1.47-6.295-2.206-10.257-2.206-7.796 0-13.772 2.368-17.925 7.095-4.154 4.728-6.677 11.31-7.573 19.747h48.506zm-48.696 14.186c.256 10.736 3.036 18.598 8.34 23.58 5.302 4.984 13.132 7.48 23.485 7.48 9.072 0 17.7-1.6 25.88-4.795 1.02 1.917 1.85 4.25 2.49 6.998a45.63 45.63 0 0 1 1.15 8.147c-4.215 1.794-8.852 3.13-13.897 4.027-5.052.892-10.643 1.342-16.774 1.342-8.95 0-16.62-1.25-23.007-3.74-6.392-2.495-11.664-6.01-15.818-10.545-4.153-4.536-7.19-9.905-9.107-16.105-1.916-6.197-2.877-13.004-2.877-20.417 0-7.285.926-14.092 2.78-20.42 1.85-6.323 4.7-11.82 8.53-16.485 3.836-4.667 8.66-8.372 14.476-11.12 5.813-2.748 12.682-4.124 20.61-4.124 6.773 0 12.716 1.152 17.83 3.452 5.11 2.3 9.393 5.464 12.845 9.49 3.45 4.027 6.07 8.82 7.86 14.377 1.788 5.562 2.686 11.6 2.686 18.12 0 1.79-.068 3.674-.195 5.654a192.677 192.677 0 0 1-.382 5.08H359.46l.002.003zm88.39-53.874a53.58 53.58 0 0 1 4.026-.574c1.275-.125 2.62-.19 4.026-.19 1.406 0 2.81.065 4.218.19 1.405.13 2.684.322 3.835.574.38 1.918.764 4.445 1.146 7.573.383 3.132.578 5.782.578 7.956 2.683-4.344 6.23-8.117 10.638-11.313 4.41-3.193 10.065-4.793 16.966-4.793 1.022 0 2.076.034 3.163.098.93.05 1.86.144 2.78.285.254 1.152.45 2.368.576 3.644.126 1.277.19 2.62.19 4.025 0 1.535-.095 3.134-.286 4.792a99.303 99.303 0 0 1-.67 4.792 13.208 13.208 0 0 0-3.165-.383h-2.59c-3.45 0-6.742.48-9.873 1.437-3.134.96-5.944 2.654-8.436 5.08-2.49 2.43-4.473 5.754-5.94 9.972-1.473 4.218-2.206 9.65-2.206 16.295v48.89c-1.555.27-3.123.463-4.698.574-1.723.128-3.29.19-4.695.19a64.51 64.51 0 0 1-4.698-.19 55.9 55.9 0 0 1-4.89-.573v-98.35zM313.3 32.12a19.054 19.054 0 0 1-1.223 6.718 19.08 19.08 0 0 1-6.72 1.224h-.028a19.06 19.06 0 0 1-6.72-1.223 19.035 19.035 0 0 1-1.225-6.72v-.03c0-2.365.434-4.63 1.22-6.72a19.018 19.018 0 0 1 6.722-1.223h.026c2.366 0 4.63.434 6.72 1.223a19.023 19.023 0 0 1 1.223 6.72v.03h.003zm23.426-5.32H318.15l13.134-13.135a31.954 31.954 0 0 0-7.502-7.5L310.646 19.3V.723A31.976 31.976 0 0 0 305.36.28h-.034c-1.802 0-3.567.154-5.287.443V19.3L286.9 6.164a31.78 31.78 0 0 0-4.06 3.436l-.006.006a32.025 32.025 0 0 0-3.433 4.06L292.54 26.8h-18.58s-.442 3.49-.442 5.294v.022c0 1.804.153 3.572.443 5.293h18.58L279.4 50.542a32.05 32.05 0 0 0 7.503 7.502L300.04 44.91v18.578c1.718.288 3.48.44 5.28.442h.045a32.11 32.11 0 0 0 5.28-.442V44.91l13.138 13.137a32.072 32.072 0 0 0 4.063-3.436h.003a32.135 32.135 0 0 0 3.432-4.063L318.147 37.41h18.58c.288-1.72.44-3.482.44-5.282v-.046c0-1.77-.148-3.535-.44-5.28V26.8zM0 180.306l51.764-80.524H6.134c-.382-2.3-.573-4.854-.573-7.667 0-2.683.194-5.178.577-7.48h73.81l.96 2.497-52.147 80.712h48.886c.383 2.557.576 5.175.576 7.858 0 2.56-.192 4.987-.575 7.287H.96L0 180.303zm149.346-43.523c-1.917-.253-4.346-.506-7.285-.765-2.94-.253-5.432-.383-7.474-.383-7.926 0-13.965 1.47-18.116 4.41-4.157 2.942-6.23 7.413-6.23 13.42 0 3.834.7 6.838 2.107 9.01 1.404 2.176 3.163 3.834 5.272 4.985 2.11 1.15 4.44 1.854 6.995 2.11 2.555.255 4.985.382 7.285.382 2.94 0 5.975-.16 9.107-.48 3.13-.317 5.91-.798 8.34-1.437v-31.252zm0-18.594c0-7.544-1.917-12.784-5.75-15.724-3.836-2.94-9.395-4.41-16.68-4.41-4.477 0-8.66.354-12.557 1.056a96.073 96.073 0 0 0-11.41 2.777c-2.43-4.218-3.64-9.263-3.64-15.146 4.34-1.404 9.132-2.49 14.375-3.257 5.24-.768 10.288-1.152 15.147-1.152 12.78 0 22.493 2.91 29.14 8.725 6.644 5.82 9.97 15.117 9.97 27.896v61.542c-4.476 1.022-9.906 2.076-16.296 3.163a116.777 116.777 0 0 1-19.555 1.63c-6.262 0-11.92-.573-16.966-1.724-5.05-1.153-9.332-3.002-12.846-5.562-3.518-2.554-6.23-5.814-8.15-9.775-1.916-3.963-2.875-8.755-2.875-14.38 0-5.494 1.118-10.32 3.355-14.476a30.233 30.233 0 0 1 9.108-10.352c3.834-2.744 8.243-4.792 13.228-6.132 4.986-1.343 10.224-2.015 15.72-2.015 4.09 0 7.445.098 10.066.29 2.618.19 4.824.414 6.614.67v-3.643zm58.28 48.692a37.62 37.62 0 0 0 7.478 1.918c2.556.385 5.876.576 9.97.576 4.6 0 8.816-.733 12.65-2.203 3.836-1.467 7.126-3.738 9.874-6.807 2.748-3.066 4.92-6.93 6.518-11.6 1.597-4.662 2.398-10.188 2.398-16.582 0-10.224-1.888-18.34-5.656-24.348-3.773-6.006-9.94-9.01-18.5-9.01-3.195 0-6.265.574-9.2 1.726-2.944 1.15-5.562 2.876-7.863 5.176-2.3 2.3-4.153 5.21-5.558 8.724-1.41 3.518-2.11 7.7-2.11 12.558v39.875-.002zm-18.98-82.247c1.3-.256 2.61-.447 3.93-.574a44.67 44.67 0 0 1 4.123-.19c1.274 0 2.615.066 4.024.19 1.404.13 2.746.323 4.026.575.126.26.288 1.055.48 2.398.19 1.343.382 2.78.575 4.312.192 1.535.383 3.008.575 4.41.192 1.408.288 2.303.288 2.686 1.275-2.043 2.81-4.026 4.6-5.943 1.79-1.918 3.933-3.643 6.425-5.175 2.492-1.535 5.302-2.748 8.435-3.644 3.13-.894 6.613-1.345 10.448-1.345 5.75 0 11.087.96 16.01 2.878 4.918 1.92 9.133 4.887 12.65 8.914 3.513 4.027 6.26 9.14 8.243 15.337 1.98 6.204 2.97 13.52 2.97 21.954 0 16.87-4.57 30.07-13.71 39.59-9.14 9.523-22.077 14.282-38.82 14.282-2.813 0-5.687-.192-8.627-.575-2.942-.385-5.496-.897-7.67-1.533v45.054a55.71 55.71 0 0 1-4.886.575c-1.727.125-3.294.19-4.7.19a65.05 65.05 0 0 1-4.696-.19 46.31 46.31 0 0 1-4.696-.575V84.635z"
        fill={props.fill || '#FF4A00'}
      />
    </svg>
  );
}

/**
 * Settings icon for the gradient settings
 */
export function IcoSwatch(props: SVGProps) {
  return (
    <svg
      class="w-4 h-4 text-gray-500"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
      />
    </svg>
  );
}

/**
 * Claude-generated icon: A mouse cursor clicking.
 */
export function IcoCursor(props: SVGProps) {
  return (
    <svg
      class="w-4 h-4 text-gray-500"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      {...props}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
      />
    </svg>
  );
}

/**
 * Claude-generated icon: A location pin.
 */
export const LocationIcon = (props: SVGProps) => (
  <svg
    class="w-6 h-6 text-red-600"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    ></path>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    ></path>
  </svg>
);
